import React, { useCallback, useEffect, useState } from 'react';
import { Modal } from 'antd';
import { ExternalLink, Loader, MapPin, RefreshCw } from 'lucide-react';
import { velocityApi } from '../../services/api';
import { velocityErrorMessage, velocityStatusLabel } from '../../utils/velocity';
import VelocityStatusPill from './VelocityStatusPill';

/**
 * Live tracking. Response Velocity ka format hai, AWB se keyed:
 *   { "<awb>": { tracking_data: { shipment_status, shipment_track_activities[], track_url } } }
 * Ye call stored statuses bhi refresh kar deta hai, isliye band karte waqt parent
 * ko refresh ka mauka dete hain.
 */
const VelocityTrackingModal = ({ open, order, onClose, onRefreshed }) => {
  const orderId = order?.id;
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [data, setData] = useState(null);

  const load = useCallback(async () => {
    if (!orderId) return;
    setLoading(true);
    setError(null);
    try {
      const res = await velocityApi.getTracking(orderId);
      setData(res);
      onRefreshed?.();
    } catch (err) {
      setError(velocityErrorMessage(err));
    } finally {
      setLoading(false);
    }
  }, [orderId, onRefreshed]);

  useEffect(() => {
    if (open) load();
  }, [open, load]);

  const entries = Object.entries(data || {});

  return (
    <Modal title="📍 Tracking" open={open} onCancel={onClose} footer={null} width={620} destroyOnClose>
      <div className="space-y-4">
        <div className="flex justify-end">
          <button
            type="button"
            className="inline-flex items-center gap-2 text-xs font-semibold text-gray-500 underline disabled:opacity-50"
            onClick={load}
            disabled={loading}
          >
            <RefreshCw className={`w-3.5 h-3.5 ${loading ? 'animate-spin' : ''}`} /> Refresh
          </button>
        </div>

        {loading && (
          <div className="py-10 flex flex-col items-center gap-3 text-gray-500">
            <Loader className="w-6 h-6 animate-spin" />
            <span className="text-sm">Fetching live status…</span>
          </div>
        )}

        {!loading && error && (
          <div className="p-3 rounded-xl bg-red-50 border border-red-200 text-sm text-red-700">{error}</div>
        )}

        {!loading && !error && entries.length === 0 && (
          <p className="py-8 text-center text-sm text-gray-500">No tracking information yet.</p>
        )}

        {!loading &&
          !error &&
          entries.map(([awb, value]) => {
            const t = value?.tracking_data || {};
            const activities = Array.isArray(t.shipment_track_activities) ? t.shipment_track_activities : [];
            return (
              <div key={awb} className="rounded-xl border border-gray-200 overflow-hidden">
                <div className="px-4 py-3 bg-gray-50 border-b border-gray-200 flex items-center justify-between gap-3">
                  <div>
                    <p className="text-[11px] font-black uppercase tracking-widest text-gray-400">AWB</p>
                    <p className="font-mono font-bold text-gray-800">{awb}</p>
                  </div>
                  <VelocityStatusPill status={t.shipment_status} />
                </div>

                <div className="p-4">
                  {activities.length === 0 ? (
                    <p className="text-sm text-gray-500">
                      No scans yet. Current status: {velocityStatusLabel(t.shipment_status)}.
                    </p>
                  ) : (
                    <ol className="space-y-3">
                      {activities.map((a, i) => (
                        <li key={`${a.date}-${i}`} className="flex gap-3">
                          <div className="flex flex-col items-center">
                            <span className={`w-2.5 h-2.5 rounded-full mt-1.5 ${i === 0 ? 'bg-gray-900' : 'bg-gray-300'}`} />
                            {i < activities.length - 1 && <span className="flex-1 w-px bg-gray-200 my-1" />}
                          </div>
                          <div className="pb-1">
                            <p className={`text-sm ${i === 0 ? 'font-bold text-gray-900' : 'font-semibold text-gray-700'}`}>
                              {a.activity}
                            </p>
                            <p className="text-xs text-gray-500 flex items-center gap-2 mt-0.5">
                              <span>{a.date}</span>
                              {a.location && (
                                <span className="inline-flex items-center gap-1">
                                  <MapPin className="w-3 h-3" /> {a.location}
                                </span>
                              )}
                            </p>
                          </div>
                        </li>
                      ))}
                    </ol>
                  )}

                  {t.track_url && (
                    <a
                      href={t.track_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="mt-4 inline-flex items-center gap-2 text-sm font-semibold text-gray-700 underline"
                    >
                      Track on courier site <ExternalLink className="w-3.5 h-3.5" />
                    </a>
                  )}
                </div>
              </div>
            );
          })}
      </div>
    </Modal>
  );
};

export default VelocityTrackingModal;
